import { UserIdentityType } from '@/services/UserIdentitiesService';
import { isMainNewDraft } from '@/screen/Identities/holderIdentityDraft';

// Reglas sobre el interviniente principal del perfil.
// Todo usuario nace con un principal vacío al registrarse en DruID (solo main = true y status = NEW, sin nombre, email
// ni teléfono). Ese principal existe, pero no identifica a nadie: las pantallas que antes miraban si había
// mainUserIdentity deben tratarlo igual que si no lo hubiera.

/**
 * Mensaje de las pantallas que crean órdenes con el principal del perfil cuando no tiene uno que identifique al usuario
 * (mismo texto que devuelve el backend en POST orders/subscription).
 */
export const UnusableMainIdentityOrderMessage = 'No dispone de un interviniente válido en el contrato para procesar esta orden.';

const isBlank = (value: unknown): boolean =>
{
    return value === undefined || value === null || String(value).trim() === '';
};

/**
 * Indica si el interviniente es el principal vacío con el que nace el usuario: en NEW y sin nombre, apellidos, email
 * ni teléfono. Un borrador NEW con algún dato ya no es vacío (lo ha empezado a rellenar el usuario o la compra).
 * El perfil local no guarda el email ni el indicador main (sanitizeUserProfile), por eso solo se mira el estado y los
 * datos.
 */
export const isBlankMainDraft = (identity: UserIdentityType | null | undefined): boolean =>
{
    if(identity === null || identity === undefined || typeof identity !== 'object')
    {
        return false;
    }

    return isMainNewDraft(identity.status)
        && isBlank(identity.firstName)
        && isBlank(identity.lastName)
        && isBlank(identity.email)
        && isBlank(identity.phone);
};

/**
 * Indica si el usuario tiene un interviniente principal que lo identifica (existe y no es el principal vacío).
 */
export const hasUsableMainIdentity = (identity: UserIdentityType | null | undefined): boolean =>
{
    return identity !== null && identity !== undefined && !isBlankMainDraft(identity);
};

/**
 * Id del interviniente principal que identifica al usuario o undefined si no lo tiene (no hay principal, no tiene id
 * o es el principal vacío).
 */
export const getUsableMainIdentityId = (identity: UserIdentityType | null | undefined): number | undefined =>
{
    if(!hasUsableMainIdentity(identity))
    {
        return undefined;
    }

    const id = Number(identity?.id ?? 0);
    return Number.isFinite(id) && id > 0 ? id : undefined;
};
